import { AutogenLayoutViewMovementList } from "./autogen/AutogenLayoutViewMovementList";
import {
    ChangeEvent, ClickEvent, Component, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DropdownItem,
    EditRowDecorator, Image as ImageComponent, Label, Layout, Panel, SaveButton, SelectionMode, SlideoutDecorator, Tab,
    TableRow, TableRowDisplayEvent, TableRowExpansionEvent, Tabset, Textbox, ValidationResult
} from "@mcleod/components";
import { getDispatchControlValue } from "@mcleod/general";
import { PlanningUtil } from "./planning/PlanningUtil";
import { MultiStop } from "./MultiStop";
import { setCommodityTooltipContent } from "@mcleod/powerbroker";
import { ViewMovementsMap } from "./ViewMovementsMap";
import { CurrencyUtil, Model, ModelRow, PermissionsUtil, StringUtil } from "@mcleod/core";
import { ExtraPay } from "./asset/ExtraPay";
import { ImageTable } from "@mcleod/common";
import { ValidationUtil } from "@mcleod/components/src/databinding/ValidationUtil";
import { ImageName } from "@mcleod/images";

interface ViewMovementsAutoSearchProps {
    search_key: string;
    search_type: ViewMovementsSearchType;
    days?: number;
    cutoff_date?: Date;
    show_voided_movements?: boolean;
}

export class ViewMovementsSearchType {
    static readonly TRACTOR = new ViewMovementsSearchType("TC", "Tractor");
    static readonly TRAILER = new ViewMovementsSearchType("TL", "Trailer");
    static readonly DRIVER = new ViewMovementsSearchType("DR", "Driver");
    static readonly ORDER = new ViewMovementsSearchType("OR", "Order");

    private constructor(public readonly value: string, public readonly displayValue: string) {}
}

export class ViewMovementList extends AutogenLayoutViewMovementList {

    private static readonly ORDER_FIELD = "order_id";
    private static readonly FALLBACK_DEFAULT_VIEW_DAYS = 15;

    private extraPayLayout: ExtraPay;
    private buttonSave: SaveButton;
    private tabset: Tabset;

    public static openSlideout(props?: ViewMovementsAutoSearchProps) {
        const layout = Layout.getLayout("lme/dispatch/ViewMovementList") as ViewMovementList;
        const slideout = new SlideoutDecorator({
            layout: layout,
            fillVerticalSpace: true,
            width: '85%',
            fillHeight: true,
            title: props ? `View Movements - ${props.search_type.displayValue} ${props.search_key}` : 'View Movements',
            displayOverlay: true,
            overlayProps: {greyedBackground: true},
            doAfterSlideIn: () => {
                layout.panelSuperHeader.margin = 10; // Allows filter panel's border to be visible
                if (props) layout.autoSearch(props);
            }
        });
    }

    private async autoSearch(props: ViewMovementsAutoSearchProps) {
        const { search_key, search_type, days, cutoff_date, show_voided_movements } = props;
        this.textboxSearchBy.selectedItem = (this.textboxSearchBy.items as DropdownItem[]).find(
            item => item.value === search_type.value
        );

        let searchTypeTextbox: Textbox;
        switch (search_type) {
            case ViewMovementsSearchType.TRACTOR:
                searchTypeTextbox = this.textboxSearchByTractor;
                break;
            case ViewMovementsSearchType.TRAILER:
                searchTypeTextbox = this.textboxSearchByTrailer;
                break;
            case ViewMovementsSearchType.DRIVER:
                searchTypeTextbox = this.textboxSearchByDriver;
                break;
            case ViewMovementsSearchType.ORDER:
                searchTypeTextbox = this.textboxSearchByOrder;
                break;
            default:
                throw new Error("Invalid search type provided.");
        }

        const search_type_field: string = searchTypeTextbox.field;
        let categoryValue: string = search_key;

        this.onChangeSearchBy(null);
        if (searchTypeTextbox.hasLookupModel()) {
            const record = await Model.searchSingleRecord(
                searchTypeTextbox.lookupModel,
                {id: search_key}
            );
            this.mainDataSource.searchRow.setLookupModelData(search_type_field, record);
            this.mainDataSource.searchRow.set(search_type_field, search_key);
            searchTypeTextbox.displayData(this.mainDataSource.searchRow, null, 0);
            if (search_type == ViewMovementsSearchType.DRIVER) {
                categoryValue = `${record.get("driver.first_name", "")} ${record.get("driver.name", "")}`;
            }
        } else {
            searchTypeTextbox.text = search_key;
        }
        this.labelCategoryValue.text = categoryValue;

        searchTypeTextbox.required = false;
        await this.mainDataSource.search({
            "type": this.textboxSearchBy.selectedItem.value,
            [search_type_field]: search_key,
            "days": days || this.textboxDays.text,
            "cutoff_date": cutoff_date || this.textboxCutoffDate.text,
            "show_voided_movements": show_voided_movements || this.switchShowVoidedMoves.checked
        });
        searchTypeTextbox.required = true;
    }

    override onLoad(): void | Promise<void> {
        this.tableResults.overrideUrlParams = (tableRow: TableRow) => {
            const orderId = !StringUtil.isEmptyString(tableRow.data.get(ViewMovementList.ORDER_FIELD)) ? tableRow.data.get(ViewMovementList.ORDER_FIELD) : "none"
            return {key: tableRow.data.get("id"), orderId: orderId}
        }

        // Initialize dropdown items
        this.textboxSearchBy.items = [
            new DropdownItem(ViewMovementsSearchType.TRACTOR.value, ViewMovementsSearchType.TRACTOR.displayValue),
            new DropdownItem(ViewMovementsSearchType.TRAILER.value, ViewMovementsSearchType.TRAILER.displayValue),
            new DropdownItem(ViewMovementsSearchType.DRIVER.value, ViewMovementsSearchType.DRIVER.displayValue),
            new DropdownItem(ViewMovementsSearchType.ORDER.value, ViewMovementsSearchType.ORDER.displayValue),
        ];
        this.textboxSearchBy.selectedItem = this.textboxSearchBy.items[0];
        this.onChangeSearchBy(null);
    }

    private onChangeSearchBy(event: ChangeEvent) {
        if (event != null && !event.userInitiatedChange) return;

        // Set inputs to their default values
        this.textboxSearchByTrailer.visible = false;
        this.setInputEnabled(this.textboxSearchByTrailer, false);
        this.textboxSearchByTractor.visible = false;
        this.setInputEnabled(this.textboxSearchByTractor, false);
        this.textboxSearchByDriver.visible = false;
        this.setInputEnabled(this.textboxSearchByDriver, false);
        this.textboxSearchByOrder.visible = false;
        this.setInputEnabled(this.textboxSearchByOrder, false);

        this.setInputEnabled(this.textboxDays, true);
        this.setInputEnabled(this.textboxCutoffDate, true);

        this.setInputEnabled(this.switchShowVoidedMoves, false);
        this.switchShowVoidedMoves.checked = true;

        // Set header panels to their default values
        this.labelCategory.text = this.textboxSearchBy.selectedItem ? this.textboxSearchBy.selectedItem.displayValue : this.textboxSearchBy.items[0].displayValue;
        this.labelCategoryValue.text = "";

        switch (this.textboxSearchBy.selectedItem.value) {
            case "TL":
                this.textboxSearchByTrailer.visible = true;
                this.setInputEnabled(this.textboxSearchByTrailer, true);
                break;
            case "TC":
                this.textboxSearchByTractor.visible = true;
                this.setInputEnabled(this.textboxSearchByTractor, true);
                break;
            case "DR":
                this.textboxSearchByDriver.visible = true;
                this.setInputEnabled(this.textboxSearchByDriver, true);
                break;
            case "OR":
                this.textboxSearchByOrder.visible = true;
                this.setInputEnabled(this.textboxSearchByOrder, true);

                this.setInputEnabled(this.textboxDays, false);
                this.textboxDays.text = getDispatchControlValue("default_view_days") ?? ViewMovementList.FALLBACK_DEFAULT_VIEW_DAYS;

                this.setInputEnabled(this.textboxCutoffDate, false);
                this.textboxCutoffDate.dateDefault = "t+3";

                this.setInputEnabled(this.switchShowVoidedMoves, true);
                break;
        }
    }

    private setInputEnabled(component: Component, enabled: boolean) {
        component.visible = enabled;
        component.enabled = enabled;
        component.required = enabled;
        component.requiredDuringSearch = enabled;
        if (!enabled && component instanceof Textbox) {
            component.clear();
        }
    }

    private onChangeCategoryValue(event: ChangeEvent) {
        if (this.textboxSearchBy.selectedItem == null) { // Reset sequence
            this.textboxSearchBy.selectedItem = this.textboxSearchBy.items[0];
            return;
        }
        if (event.target == this.textboxSearchByTrailer && "TL" === this.textboxSearchBy.selectedItem.value) {
            this.labelCategoryValue.text = event.newValue;
        }
        if (event.target == this.textboxSearchByTractor && "TC" === this.textboxSearchBy.selectedItem.value) {
            this.labelCategoryValue.text = event.newValue;
        }
        if (event.target == this.textboxSearchByDriver && "DR" === this.textboxSearchBy.selectedItem.value) {
            this.labelCategoryValue.text = event.newValue;
        }
        if (event.target == this.textboxSearchByOrder && "OR" === this.textboxSearchBy.selectedItem.value) {
            this.labelCategoryValue.text = event.newValue;
        }
    }

    private validateFilters(): boolean {
        if (!this.textboxSearchBy.validateSimple(true, true)) {
            this.textboxSearchBy.focus();
            return false;
        }
        if (!this.textboxSearchByTrailer.validateSimple(true, true)) {
            this.textboxSearchByTrailer.focus();
            return false;
        }
        if (!this.textboxSearchByTractor.validateSimple(true, true)) {
            this.textboxSearchByTractor.focus();
            return false;
        }
        if (!this.textboxSearchByDriver.validateSimple(true, true)) {
            this.textboxSearchByDriver.focus();
            return false;
        }
        if (!this.textboxSearchByOrder.validateSimple(true, true)) {
            this.textboxSearchByOrder.focus();
            return false;
        }
        if (!this.textboxSearchByOrder.validateSimple(true, true)) {
            this.textboxSearchByOrder.focus();
            return false;
        }
        if (!this.textboxDays.validateSimple(true, true)) {
            this.textboxDays.focus();
            return false;
        }
        if (!this.textboxCutoffDate.validateSimple(true, true)) {
            this.textboxCutoffDate.focus();
            return false;
        }
        if (!this.switchShowVoidedMoves.validateSimple(true, true)) {
            this.switchShowVoidedMoves.focus();
            return false;
        }
        return true;
    }

    private beforeSearch(event: DataSourceExecutionEvent) {
        if (!this.validateFilters()) {
            event.preventDefault();
            return;
        }
        event.filter.show_voided_movements = this.switchShowVoidedMoves.checked;
    }

    private afterSearch(event: DataSourceExecutionEvent) {
        if (event.getAction() != DataSourceAction.SEARCH) return;

        const summary = event.dataSource.summaryData;
        this.labelMileageLoaded.text = summary.get("distance_loaded");
        this.labelMileageEmpty.text = summary.get("distance_empty");
        this.labelMileageDeadhead.text = summary.get("distance_percent_deadhead");
        this.labelMileageTotal.text = summary.get("distance_total");

        const revenuePerMileLoaded = summary.getCurrency("revenue_per_mile_loaded");
        this.labelRatesPerMileLoaded.text = CurrencyUtil.formatCurrency(revenuePerMileLoaded)

        const revenuePerMileTotal = summary.getCurrency("revenue_per_mile_total");
        this.labelRatesPerMileTotal.text = CurrencyUtil.formatCurrency(revenuePerMileTotal)

        const revenueTotal = summary.getCurrency("revenue_total");
        this.labelRatesTotal.text = CurrencyUtil.formatCurrency(revenueTotal)
    }

    private viewOrder(orderId: string) {
        orderId = orderId.replace(',', '');
        PlanningUtil.navigateToOrderId(orderId);
    }

    private async onRowExpand(expansionEvent: TableRowExpansionEvent) {
        const tableRow: TableRow = expansionEvent.target as TableRow;
        const expandComponent = await tableRow.getExpansionComponentLoaded() as Panel;
        const map = expandComponent.findComponentById("layoutViewMovementsMap") as ViewMovementsMap;
        map.addPinsForMovement(tableRow?.data?.get("id"));
        this.tabset = expandComponent.findComponentById("tabsetRowExpand") as Tabset;
        this.tabset.addAfterTabSelectionListener((event) => {
            const tab = this.tabset.getActiveTab();
            if (this.buttonSave)
                this.buttonSave.visible = (tab.caption === "Settlement Details");
        });
        if(tableRow?.data?.isNull("transfer_to_settlements") && !tableRow?.data.isNull("driver_1_id") &&
        !PermissionsUtil.isUserDeniedLayout("mcleod-api-lme/lme/dispatch/asset/ExtraPay") && !PermissionsUtil.isUserDeniedLayout("lme/dispatch/asset/ExtraPay")) {
            this.extraPayLayout = expandComponent.findComponentById("layoutExtraPay") as ExtraPay;
            await Promise.all([
                this.extraPayLayout.sourceMovement.search({"id" : tableRow?.data?.get("id")}),
                this.extraPayLayout.sourceExtraPay.search({"movement_id" : tableRow?.data?.get("id")})
            ]);
            this.buttonSave = this.tabset.afterTools[0] as SaveButton;
            this.buttonSave.dataSource = this.extraPayLayout.sourceMovement;
            this.configureExtraPay(tableRow);
        }
        else {
            const tabExtraPay = expandComponent.findComponentById("tabExtraPay") as Tab
            tabExtraPay.visible = false;
            tabExtraPay.enabled = false;
        }

        const imageTable = expandComponent.findComponentById("layoutImageTable") as ImageTable;

        if (!tableRow?.data?.isNull("order_id")) {
        imageTable.populateForRow("O", tableRow?.data.get("order_id"));
        }
        else {
            const tabImages = expandComponent.findComponentById("tabImages") as Tab
            tabImages.visible = false;
            tabImages.enabled = false;
        }
    }

    private onRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        if (!tableRow.data) return;

        const modelRow = tableRow.data;

        this.splitOrdersIfApplicable(tableRow, modelRow);
        this.addStatusIcon(tableRow, modelRow);
        this.addSplitIcon(tableRow, modelRow);
        this.addTransferBadge(tableRow, modelRow);
        this.addServiceIncidentBadge(tableRow, modelRow);
        this.addLoadedEmptyIcon(tableRow, modelRow);
        this.addMultiStopIcon(tableRow);
        this.addDetentionIcon(tableRow, modelRow);
        this.setDriverName(tableRow, modelRow);
        this.setCommodityName(tableRow, modelRow)

    }

    private configureExtraPay(tableRow: TableRow) {
        const driverIds: string[] = [];
        const driverOneId = tableRow?.data?.get("driver_1_id");
        if((!StringUtil.isEmptyString(driverOneId)))
            driverIds.push(driverOneId);
        const driverTwoId = tableRow?.data?.get("driver_2_id");
        if((!StringUtil.isEmptyString(driverTwoId)))
            driverIds.push(driverTwoId);
        this.extraPayLayout.setDriverIds(driverIds);

        this.extraPayLayout.endDate = tableRow?.data?.get("end_date") as Date;

        const tableExtraPay = this.extraPayLayout.tableExtraPay
        tableExtraPay.dataSource.mode = DataSourceMode.UPDATE;
        tableExtraPay.selectionMode = SelectionMode.SINGLE;
        tableExtraPay.persistChangesImmediately = true;
    }

    private splitOrdersIfApplicable(tableRow: TableRow, modelRow: ModelRow) {
        const labelOrderId = tableRow.findComponentById("labelOrderId") as Label;

        const orderIds = modelRow.get("order_id");
        if (orderIds) {
            const orderIdArray = orderIds.split(',').map(id => id.trim());
            for (let i = 0; i < orderIdArray.length; i++) {
                const newLabel = new Label({
                    text: orderIdArray[i] + (i < orderIdArray.length - 1 ? "," : ""),
                    onClick: (event) => this.viewOrder((event.target as Label).text),
                    tooltip: "View Order",
                    style: {marginLeft: "2px"},
                    rowBreak: false
                });
                newLabel.addUnderlineWhenMouseOver();
                labelOrderId.parent.insert(newLabel, i);
            }
            labelOrderId.visible = false;
        }
    }

    private addStatusIcon(tableRow: TableRow, modelRow: ModelRow) {
        const imageStatus: ImageComponent = tableRow.findComponentById("imageStatus") as ImageComponent;
        const status = modelRow.get("status");

        const statusIcons: { [key: string]: ImageName } = {
            "Delivered": ImageName.loadDelivery,
            "Void": ImageName.loadVoided,
            "In Progress": ImageName.loadInProgress,
            "Available": ImageName.loadAvailable,
            "Covered": ImageName.loadCovered
        };

        imageStatus.tooltip = status;
        imageStatus.name = statusIcons[status] || null;
        imageStatus.fill = "primary";
    }

    private addSplitIcon(tableRow: TableRow, modelRow: ModelRow) {
        const imagSplit: ImageComponent = tableRow.findComponentById("imageSplit") as ImageComponent;
        const ySplit = modelRow.get("y_split");

        if (StringUtil.isEmptyString(ySplit)) {
            return; // No split
        }

        const splitTooltips: { [key: string]: string } = {
            "ySplitComplete": "Y-Split Complete",
            "ySplitPick": "Y-Split Pick",
            "ySplit": "Y-Split"
        };

        imagSplit.tooltip = splitTooltips[ySplit];
        imagSplit.name = ySplit;
    }

    private addTransferBadge(tableRow: TableRow, modelRow: ModelRow) {
        const wasTransferred = modelRow.get("transfer_to_settlements");
        if (!StringUtil.isEmptyString(wasTransferred)) {
            const dateMatch = wasTransferred.match(/^(\d{4})-(\d{2})-(\d{2})/);
            const badge: ImageComponent = tableRow.findComponentById("imageXferToSettlements") as ImageComponent;
            badge.tooltip = dateMatch
                ? `Transferred to Settlements on ${dateMatch[2]}/${dateMatch[3]}/${dateMatch[1]}`
                : `Transferred to Settlements on ${wasTransferred}`;
            badge.name = ImageName.transferredToSettlements;
        }
    }

    private addServiceIncidentBadge(tableRow: TableRow, modelRow: ModelRow) {
        if (modelRow.get("service_incidents")) {
            const badge = tableRow.findComponentById("imageServiceIncident") as ImageComponent;
            badge.name = ImageName.wrench;
        }
    }

    private addLoadedEmptyIcon(tableRow: TableRow, modelRow: ModelRow) {
        const imageLoaded = tableRow.findComponentById("imageLoaded") as ImageComponent;
        imageLoaded.name = modelRow.get("loaded") ? ImageName.circleCheckEmpty : ImageName.emptyMove;
        imageLoaded.tooltip = modelRow.get("loaded") ? "Loaded" : "Empty";
    }

    private addMultiStopIcon(tableRow: TableRow) {
        const labelStopsPickDrop = tableRow.findComponentById("labelStopsPickDrop") as Label;
        labelStopsPickDrop.applyLinkStyle(true);
    }

    private addDetentionIcon(tableRow: TableRow, modelRow: ModelRow) {
        const imageDetention = tableRow.findComponentById("imageDetention") as ImageComponent;
        if (imageDetention)
            imageDetention.visible = modelRow.getBoolean("loaded", false)
                && modelRow.getBoolean("has_detention", false);
    }

    private setDriverName(tableRow: TableRow, modelRow: ModelRow) {
        const driver1Name = modelRow.get("driver_1");
        const labelDriver1Name = tableRow.findComponentById("labelDriver") as Label;
        labelDriver1Name.text = driver1Name;

        const driver2Name = modelRow.get("driver_2");
        const labelDriver2Name = tableRow.findComponentById("labelDriver2") as Label;
        labelDriver2Name.text = driver2Name;
    }

    private setCommodityName(tableRow: TableRow, modelRow: ModelRow) {
        const labelCommodity = tableRow.findComponentById("labelCommodity") as Label;
        const commodityId = modelRow.get("commodity_id");
        if (commodityId === "?") {
            labelCommodity.quickInfoLayout = null;
        }
        labelCommodity.text = modelRow.get("commodity");
    }

    private showMultiStopSlideout(event: ClickEvent): void {
        const panel = event.target as Panel;
        panel.busy = true;
        try {
            const tableRow: TableRow = TableRow.getContainingTableRow(panel);
            tableRow.table.selectedRow = tableRow;
            const row = tableRow.data;
            this.createEditRowDecorator(row.get(ViewMovementList.ORDER_FIELD), row.get("manifest_id"), row.get("id"), panel);
        } finally {
            panel.busy = false;
        }
    }

    private async createEditRowDecorator(orderId: string, manifestId: string, movementId: string,  panel: Panel): Promise<void> {
        const isManifest = !StringUtil.isEmptyString(manifestId);
        const multiStopLayout = Layout.getLayout("lme/dispatch/MultiStop") as MultiStop;
        const searchInput = isManifest ? { manifest_id: manifestId, movement_id: movementId } : { id: orderId };
        const erd = new EditRowDecorator({
            title: isManifest ? `Stop Details - Manifest ${manifestId}` : `Stop Details - Order ${orderId}`,
            layout: multiStopLayout,
            layoutLoadListeners: async () => {
                if (isManifest) {
                    multiStopLayout.mainDataSource = multiStopLayout.sourceManifestMultistop;
                    multiStopLayout.sourceStop.parentDataSource = multiStopLayout.sourceManifestMultistop;
                    multiStopLayout.sourceStop.orderBy = [{ field: "movement_sequence", sort: "asc" }];
                    multiStopLayout.isManifest = true;
                }
                await multiStopLayout.mainDataSource.search(searchInput);
                multiStopLayout.mainDataSource.mode = DataSourceMode.UPDATE;
                multiStopLayout.activeRow.set(ViewMovementList.ORDER_FIELD, orderId);
                if (isManifest) {
                    multiStopLayout.panelOrderInfo.visible = false;
                    multiStopLayout.panelManifestInfo.visible = true;
                }
                const summaryData = multiStopLayout.mainDataSource.summaryData;
                if (isManifest && summaryData?.get("customers").length === 1) {
                    multiStopLayout.labelManifestCustAddress1.visible = true;
                    multiStopLayout.citystateManifestCust.visible = true;
                }
                setCommodityTooltipContent(multiStopLayout.textboxCommodityId, multiStopLayout.activeRow);
            },
            width: '80%',
            overlayProps: {closeOnClickOff: false, greyedBackground: true},
            onSave: async () => {
                multiStopLayout.postChanges();
            },
            onClose: () => multiStopLayout.slideOut(),
            doAfterSlideIn: () => panel.busy = false,
            onValidate: () => {
                const validationResults:ValidationResult[] = multiStopLayout.validate(true, true);
                if (!ValidationUtil.checkValidationResults(validationResults)) {
                    ValidationUtil.showFailedValidationDialog(validationResults.filter(result => !result.isValid));
                }
                return validationResults;
            }
        });
    }
}
